import { useTranslation } from 'react-i18next';
import { parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/common/Icon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import {
  buildAppointmentStatusTranslationKey,
  buildTherapyTypeTranslationKey,
} from '@/constants/appointmentOptions';
import { resolveAppointmentColorClasses } from '@/constants/appointmentColors';
import { formatDateToDayMonthYear } from '@/utils/dateFormatting';
import {
  isAppointmentCompleted,
  resolveAppointmentStatusCode,
  resolveCaretakerDisplayName,
  resolvePatientDisplayName,
} from '../utils/appointmentDisplay';
import type { Appointment } from '@/api/appointments/appointmentsApi.types';
import { ClientRequestNotice } from './ClientRequestNotice';

interface AppointmentPreviewModalProps {
  appointment: Appointment;
  onClose: () => void;
  onEdit: (appointment: Appointment) => void;
  onOpenSessionNote: (appointment: Appointment) => void;
}

export function AppointmentPreviewModal({
  appointment,
  onClose,
  onEdit,
  onOpenSessionNote,
}: AppointmentPreviewModalProps) {
  const { t } = useTranslation();
  const colorClasses = resolveAppointmentColorClasses(appointment.therapyType);
  const patientFallbackText = t('appointments:patientFallback');
  const caretakerUnknownText = t('appointments:caretakerUnknown');

  return (
    <ModalOverlay onRequestClose={onClose}>
      <div className="bg-zoowhite w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        <div className="p-6 border-b border-zooborder flex justify-between items-center bg-zoovpalelavender bg-opacity-30">
          <h3 className="text-lg font-bold text-zooindigo">
            {t('appointments:modal.previewTitle')}
          </h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label={t('common:actions.close')}
          >
            <Icon name="X" className="text-zooindigo" />
          </Button>
        </div>

        <div className="p-6 space-y-4">
          <ClientRequestNotice appointment={appointment} onDismissed={onClose} />
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={cn(
                'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border',
                colorClasses.softBg,
                colorClasses.accentBorder,
                colorClasses.accentText
              )}
            >
              <span className={cn('w-2 h-2 rounded-full', colorClasses.accentDot)} />
              {t(buildTherapyTypeTranslationKey(appointment.therapyType))}
            </span>
            <Badge
              tone={isAppointmentCompleted(appointment) ? 'success' : 'neutral'}
              size="md"
              uppercase
            >
              {t(buildAppointmentStatusTranslationKey(resolveAppointmentStatusCode(appointment)))}
            </Badge>
          </div>

          <PreviewRow
            label={t('appointments:fields.date')}
            value={formatDateToDayMonthYear(parseISO(appointment.date))}
          />
          <PreviewRow
            label={t('appointments:fields.from') + ' – ' + t('appointments:fields.to')}
            value={
              appointment.endTime.length > 0
                ? appointment.startTime + ' – ' + appointment.endTime
                : appointment.startTime
            }
          />
          <PreviewRow
            label={t('appointments:fields.patient')}
            value={resolvePatientDisplayName(appointment, patientFallbackText)}
          />
          <PreviewRow
            label={t('appointments:fields.caretaker')}
            value={resolveCaretakerDisplayName(appointment, caretakerUnknownText)}
          />
          {appointment.note.length > 0 ? (
            <PreviewRow label={t('appointments:fields.note')} value={appointment.note} />
          ) : null}
        </div>

        <div className="p-6 border-t border-zooborder flex justify-end gap-3 flex-wrap">
          <Button variant="outline" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
          <Button
            variant="outlinePrimary"
            onClick={function openNote() {
              onOpenSessionNote(appointment);
            }}
            className="flex items-center gap-2"
          >
            <Icon name="FileText" /> {t('patients:sessionNote.editTitle')}
          </Button>
          <Button
            onClick={function editThisAppointment() {
              onEdit(appointment);
            }}
            className="flex items-center gap-2"
          >
            <Icon name="Edit" /> {t('common:actions.edit')}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-zoomuted">{label}</div>
      <div className="text-sm text-zooindigo mt-0.5 whitespace-pre-wrap">{value}</div>
    </div>
  );
}
